import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, ArrowRight, Loader2 } from 'lucide-react';

import { submitCallback } from '../../lib/callback';
import { trackEvent } from '../../lib/analytics';

const DISMISS_KEY = 'scroll_cta_dismissed_at';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

export default function ScrollDepthCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof localStorage === 'undefined') return false;
    const stored = localStorage.getItem(DISMISS_KEY);
    if (!stored) return false;
    return Date.now() - Number(stored) < DISMISS_DURATION_MS;
  });
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const sending = useRef(false);
  const shownRef = useRef(false);

  useEffect(() => {
    if (isDismissed) return;

    const handleScroll = () => {
      const scrolled = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (scrolled >= 0.7 && !shownRef.current) {
        shownRef.current = true;
        setIsVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDismissed]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setIsDismissed(true);
    setIsVisible(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending.current) return;
    sending.current = true;
    setStatus('submitting');
    trackEvent('callback_submit', { source: 'scroll_depth_cta' });
    try {
      await submitCallback({ name, phone, source: 'scroll_depth_cta' });
      setStatus('done');
      trackEvent('callback_success', { source: 'scroll_depth_cta' });
    } catch {
      setStatus('error');
      trackEvent('callback_error', { source: 'scroll_depth_cta' });
    } finally {
      sending.current = false;
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ x: '-110%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '-110%', opacity: 0 }}
          transition={{ type: 'spring', damping: 28, stiffness: 260 }}
          className="hidden lg:block fixed bottom-8 left-6 z-50 w-80"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#d4a10d] to-[#b8900b] px-5 py-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-white font-bold text-base leading-tight">
                  Still deciding?
                </p>
                <p className="text-white/85 text-sm mt-0.5">
                  We'll call you, no forms, no fluff.
                </p>
              </div>
              <button
                onClick={dismiss}
                className="text-white/70 hover:text-white transition-colors p-1 flex-shrink-0 mt-0.5"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4">
              {status === 'done' ? (
                <div role="status" className="text-center py-3">
                  <p className="text-gray-900 font-bold text-base mb-1">You're on the list.</p>
                  <p className="text-gray-500 text-sm">We'll call within one business day.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  <label htmlFor="scroll-callback-name" className="sr-only">First name</label>
                  <input
                    id="scroll-callback-name"
                    autoComplete="given-name"
                    maxLength={80}
                    type="text"
                    required
                    placeholder="First name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#d4a10d]/60 transition-all"
                  />
                  <label htmlFor="scroll-callback-phone" className="sr-only">Phone number</label>
                  <input
                    id="scroll-callback-phone"
                    autoComplete="tel"
                    maxLength={30}
                    type="tel"
                    required
                    placeholder="+1 XXX XXX XXXX"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#d4a10d]/60 transition-all"
                  />
                  {status === 'error' && <p role="alert" className="text-red-700 text-sm">We couldn’t confirm your request. Check your phone number and try again, or call us below.</p>}
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full flex items-center justify-center gap-2 bg-[#d4a10d] hover:bg-[#b8940b] disabled:opacity-60 text-white font-bold py-2.5 rounded-lg text-sm transition-all duration-200"
                  >
                    {status === 'submitting' ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                    ) : (
                      <>Call me back <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>
                </form>
              )}
              <div className="flex items-center justify-center gap-1.5 mt-3">
                <Phone className="w-3 h-3 text-gray-400" />
                <a href="tel:7096498492" className="text-xs text-gray-400 hover:text-[#d4a10d] transition-colors">
                  (709) 649-8492
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
